/** Numbered DEMO WALK — same 1–4 script as admin Live jobs. Assign/complete stay Function-only. */

import {
    PWA_PENDING_ENTRY_JOBS_SOT,
    pwaJobsSotHref,
    pwaLiveJobsEntryHref,
    pwaStatusFromSearch,
} from './ordersSurface';

export type PwaDemoWalkStepId = 'seed' | 'pending' | 'assign' | 'complete';

export type PwaDemoWalkStep = {
    id: PwaDemoWalkStepId;
    n: number;
    label: string;
    detail: string;
    href?: string | null;
};

export type PwaDemoWalkSotLink = {
    id: 'pendingHandoff' | 'handoff' | 'completeHandoff';
    label: string;
    href: string;
};

function searchParams(search = ''): URLSearchParams {
    const raw = String(search).startsWith('?') ? String(search).slice(1) : String(search);
    return new URLSearchParams(raw);
}

function isPwaPendingEntryJobsSot(value: string | null): boolean {
    return Boolean(value && (PWA_PENDING_ENTRY_JOBS_SOT as readonly string[]).includes(value));
}

/** DEMO WALK Open a Pending row. Mint pending-handoff unless a leftover pending fixture is already on. */
export function pwaDemoWalkPendingHref(search = ''): string {
    if (isPwaPendingEntryJobsSot(searchParams(search).get('jobsSot'))) {
        return pwaLiveJobsEntryHref(search);
    }
    return pwaJobsSotHref('pending-handoff', search);
}

export const PWA_DEMO_WALK_ORDER: PwaDemoWalkStepId[] = ['seed', 'pending', 'assign', 'complete'];

export function pwaDemoWalkSteps(search = ''): PwaDemoWalkStep[] {
    return [
        { id: 'seed', n: 1, label: 'Seed pending', detail: 'shop createIntake or admin /dev-intake', href: null },
        { id: 'pending', n: 2, label: 'Open a Pending row', detail: 'this Pending tab', href: pwaDemoWalkPendingHref(search) },
        { id: 'assign', n: 3, label: 'Assign via assignJob', detail: '— this Active tab follows', href: null },
        { id: 'complete', n: 4, label: 'Complete via completeJob', detail: '— admin Complete follows', href: null },
    ];
}

export function pwaDemoWalkKeepNote(): string {
    return 'Same 1–4 script as shop + admin Live jobs. Cross-surface E2E: shop createIntake → admin Pending → assignJob → this Active tab → completeJob. No hard-refresh thrash. Current-step fill stays through assignJob → completeJob. Active → Complete keeps step 4 filled. Pending tab uses pwaLiveJobsEntryHref (admin scoreboardLiveJobsEntryHref contract): leftover jobsSot=pending / pending-seed / pending-handoff stay so entry is not Seed; Active / Complete jobsSot is dropped so the tab is not wearing handoff / complete-handoff. DEMO WALK Open a Pending row mints jobsSot=pending-handoff when there is no leftover pending fixture so the step paints a Demo Camry pending row — not PENDING · EMPTY and not leftover Active handoff. jobsSot=pending-handoff paints Pending (step 2). jobsSot=pending is still PENDING · EMPTY. jobsSot=handoff paints Active (step 4). jobsSot=complete-handoff paints Complete (step 4). A jobsSot-only URL writes the matching ?status= chip so fill does not fall to Seed. Region switch colorado2 ↔ Test Region keeps ?status= and jobsSot — it is not Exit SoT. DEMO WALK rebinds to the named region (not preview-test-region). Admin Complete follows with a named region and exclusive counts — no bounce to /regions. Shop jobsSot=pending-handoff seeds Pending. Exit SoT drops jobsSot / createSot so Seed and mid-walk fixtures re-enter clean. Only assignJob writes active. Only completeJob writes complete. Never ADAS.';
}

export function pwaDemoWalkNote(): string {
    return 'Demo walk: 1. Seed pending on shop createIntake or Intake fixture  2. Open a Pending row  3. Assign via assignJob (this Active tab follows)  4. Complete via completeJob (this Complete tab + admin Complete follow). Current-step fill stays through the handoff. Admin Complete keeps the named region and exclusive counts — no bounce to /regions.';
}

function walkStepFromStatus(status?: string | null): PwaDemoWalkStepId | null {
    if (status === 'pending') return 'pending';
    if (status === 'active' || status === 'complete') return 'complete';
    return null;
}

export function pwaDemoWalkCurrentStep(input: {
    search?: string;
    openTab?: number;
    jobStatus?: string;
    sot?: string | null;
    preview?: string | null;
} = {}): PwaDemoWalkStepId {
    const fixture = input.sot || input.preview || '';
    if (fixture === 'complete-handoff' || fixture === 'handoff') return 'complete';
    if (fixture === 'pending-handoff') return 'pending';
    const fromJob = walkStepFromStatus((input.jobStatus || '').trim());
    if (fromJob) return fromJob;
    const fromSearch = walkStepFromStatus(pwaStatusFromSearch(input.search));
    if (fromSearch) return fromSearch;
    if (input.openTab === 3) return 'complete';
    if (input.openTab === 2) return 'complete';
    if (input.openTab === 1 || fixture === 'pending') return 'pending';
    return 'assign';
}

export function pwaDemoWalkStepState(
    id: PwaDemoWalkStepId,
    current: PwaDemoWalkStepId,
): 'current' | 'done' | 'todo' {
    const index = PWA_DEMO_WALK_ORDER.indexOf(id);
    const currentIndex = PWA_DEMO_WALK_ORDER.indexOf(current);
    if (index === currentIndex) return 'current';
    if (index >= 0 && currentIndex >= 0 && index < currentIndex) return 'done';
    return 'todo';
}

export function pwaDemoWalkStepClass(
    id: PwaDemoWalkStepId,
    current: PwaDemoWalkStepId,
): 'is-current' | 'is-done' | undefined {
    const state = pwaDemoWalkStepState(id, current);
    if (state === 'current') return 'is-current';
    if (state === 'done') return 'is-done';
    return undefined;
}

export function pwaDemoWalkSotLinks(search = ''): PwaDemoWalkSotLink[] {
    return [
        { id: 'pendingHandoff', label: 'jobsSot pending-handoff', href: pwaJobsSotHref('pending-handoff', search) },
        { id: 'handoff', label: 'jobsSot handoff', href: pwaJobsSotHref('handoff', search) },
        { id: 'completeHandoff', label: 'jobsSot complete-handoff', href: pwaJobsSotHref('complete-handoff', search) },
    ];
}

/** Tab for a Hosting fixture or ?status= chip. Exit / unknown → Pending (1) so walk fill is not stuck on Complete. */
export function pwaDemoWalkOpenTab(input: {
    sot?: string | null;
    preview?: string | null;
    search?: string;
    status?: string | null;
} = {}): number {
    const status = input.status || pwaStatusFromSearch(input.search);
    if (status === 'complete') return 3;
    if (status === 'active') return 2;
    if (status === 'pending') return 1;
    const fixture = input.sot || input.preview || '';
    if (fixture === 'complete-handoff') return 3;
    if (fixture === 'handoff') return 2;
    if (fixture === 'pending-handoff') return 1;
    return 1;
}

export function pwaDemoWalkEyebrow(regionLabel: string): string {
    const label = regionLabel.trim() || 'glass-pwa';
    return `DEMO WALK · ${label}`;
}

export function shouldResetPwaWalkResidue(input: {
    previousSot?: string | null;
    nextSot?: string | null;
    previousCreateSot?: string | null;
    nextCreateSot?: string | null;
} = {}): boolean {
    return (input.previousSot || null) !== (input.nextSot || null)
        || (input.previousCreateSot || null) !== (input.nextCreateSot || null);
}

export function pwaDemoWalkStampLine(stamp: string): string {
    const value = stamp.trim() || 'unknown';
    return `pwa build ${value}`;
}

export function pwaDemoWalkStampHint(): string {
    return 'Hard refresh if the walk looks stale · Cmd/Ctrl+Shift+R';
}
